<template>
  <footer class="footer">
    <div class="container footer__main">
      <div class="footer__brand">
        <img
          :src="data.site.logo"
          alt="Centre Sud"
        >
        <p class="footer__address">{{ addressLines[0] }}<br>{{ addressLines[1] }}</p>
      </div>

      <nav class="footer__links">
        <a
          v-for="item in data.nav.items"
          :key="item.anchor"
          :href="item.anchor"
          @click.prevent="goTo(item.anchor)"
        >{{ item.label }}</a>
      </nav>

      <div class="footer__newsletter">
        <p class="footer__newsletter-label">{{ data.footer.newsletterLabel }}</p>
        <form
          v-if="!subscribed"
          class="footer__newsletter-form"
          @submit.prevent="subscribe"
        >
          <input
            v-model.trim="email"
            type="email"
            :placeholder="data.footer.newsletterPlaceholder"
            required
          >
          <button
            type="submit"
            aria-label="S'inscrire"
            :disabled="sending"
          >→</button>
        </form>
        <p
          v-else
          class="footer__newsletter-success"
        >{{ pending ? data.footer.newsletterPending : data.footer.newsletterSuccess }}</p>
        <p
          v-if="error"
          class="footer__newsletter-error"
        >{{ error }}</p>
        <p
          v-if="data.footer.newsletterConsent && !subscribed"
          class="footer__newsletter-consent"
        >
          {{ data.footer.newsletterConsent }}
          <router-link
            v-if="privacyLink"
            :to="privacyLink.href"
          >{{ privacyLink.label }}</router-link>
        </p>
      </div>
    </div>

    <div class="footer__bottom">
      <div class="container footer__bottom-inner">
        <span>{{ data.footer.copyright }}</span>
        <!-- sur mobile, ces liens passent dans le menu (cf. maquette mobile) -->
        <router-link
          v-for="link in data.footer.legalLinks"
          :key="link.label"
          class="footer__legal"
          :to="link.href"
        >{{ link.label }}</router-link>
      </div>
    </div>
  </footer>
</template>

<script>
import axios from 'axios'
import { STRAPI_URL } from '@/content/ccContent'
import { scrollToAnchor } from '@/utils/scroll'

export default {
  name: 'SiteFooter',
  props: {
    data: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      email: '',
      subscribed: false,
      // double opt-in : Mailchimp a envoyé un e-mail de confirmation
      pending: false,
      sending: false,
      error: ''
    }
  },
  computed: {
    addressLines() {
      return (this.data.acces.address || '').split('\n')
    },
    privacyLink() {
      return (this.data.footer.legalLinks || []).find((l) => /confidentialit/i.test(l.label)) || null
    }
  },
  methods: {
    goTo(anchor) {
      if (this.$route.path !== '/') {
        this.$router.push({ path: '/', hash: anchor })
        return
      }
      scrollToAnchor(anchor)
    },
    async subscribe() {
      if (!this.email || this.sending) return
      this.sending = true
      this.error = ''
      try {
        // Strapi configuré : on enregistre l'inscription. Sinon confirmation côté front seul.
        if (STRAPI_URL) {
          const res = await axios.post(`${STRAPI_URL}/api/newsletter/subscribe`, { email: this.email })
          this.pending = !!res.data?.pending
        }
        this.subscribed = true
      } catch (err) {
        console.warn('[centre-sud] inscription newsletter échouée', err?.message || err)
        this.error = this.data.footer.newsletterError || 'Inscription impossible pour le moment.'
      } finally {
        this.sending = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.footer {
  background: $bg-light;
}

.footer__main {
  display: grid;
  grid-template-columns: 1fr 1fr 1.2fr;
  gap: clamp(24px, 5vw, 80px);
  padding: 52px 0 40px;
}

.footer__brand img {
  height: 74px;
  width: auto;
}

.footer__address {
  margin-top: 20px;
  font-size: 13.5px;
  line-height: 1.6;
}

.footer__links {
  display: flex;
  flex-direction: column;
  gap: 10px;

  a {
    font-size: 13.5px;

    &:hover {
      color: $cyan;
    }
  }
}

.footer__newsletter-label {
  font-size: 13.5px;
  margin-bottom: 12px;
}

.footer__newsletter-form {
  display: flex;
  background: $white;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba($navy, 0.06);

  input {
    flex: 1;
    border: none;
    padding: 13px 16px;
    font-family: inherit;
    font-size: 13.5px;
    color: $ink;

    &:focus {
      outline: none;
    }

    &::placeholder {
      color: rgba($ink, 0.45);
    }
  }

  button {
    border: none;
    background: none;
    padding: 0 18px;
    font-size: 16px;
    color: $ink;
    transition: color 0.2s ease;

    &:hover {
      color: $cyan;
    }
  }
}

.footer__newsletter-success {
  font-size: 13.5px;
  color: $green;
  font-weight: 600;
}

.footer__newsletter-consent {
  margin-top: 10px;
  font-size: 11.5px;
  line-height: 1.45;
  color: rgba($ink, 0.65);

  a {
    text-decoration: underline;

    &:hover {
      color: $cyan;
    }
  }
}

.footer__newsletter-error {
  margin-top: 8px;
  font-size: 12.5px;
  color: #C0392B;
  font-weight: 600;
}

// bandeau légal : fond légèrement plus soutenu que le reste du pied de page
.footer__bottom {
  background: $bg-footer-bar;
  padding: 16px 0;
}

.footer__bottom-inner {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 40px;
  font-size: 12px;
  color: rgba($ink, 0.75);

  a:hover {
    color: $cyan;
  }
}

// Maquette mobile : newsletter en tête, puis logo + adresse centrés, liens sur
// deux colonnes ; le bandeau du bas ne garde que le copyright.
@include mobile {
  .footer__main {
    grid-template-columns: 1fr;
    gap: 30px;
    padding: 36px 0 30px;
    text-align: center;
  }

  .footer__newsletter {
    order: -1;
  }

  .footer__newsletter-label {
    font-size: 17px;
    font-weight: 600;
  }

  .footer__newsletter-form input {
    text-align: left;
  }

  .footer__brand img {
    margin: 0 auto;
    height: 90px;
  }

  .footer__address {
    font-size: 14.5px;
  }

  .footer__links {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-auto-flow: column;
    grid-template-rows: repeat(3, auto);
    gap: 2px 12px;

    a {
      padding: 7px 0;
      font-size: 14.5px;
    }
  }

  .footer__bottom-inner {
    justify-content: center;
  }

  .footer__legal {
    display: none;
  }
}
</style>
